/**
 * The Document Studio shell — one editor for both artifacts (#241 candidate
 * B4), per DESIGN.md "Settings studios" and ADR-0010.
 *
 * The artifact is the server-rendered HTML twin, injected in page: block
 * regions edit in place on the sheet (a × on every present section, an
 * add-strip pill for every removed one, committed through one delegated
 * click handler), while values woven into the document's running copy edit
 * as ordinary labelled fields beside it — the `fields` slot. Renaming lives
 * in the editor's title and commits on blur. There are no save buttons:
 * every interaction is its own commit, and a failed one reverts.
 *
 * `useTemplateStudio` owns the commits, `StudioShell` the chrome; an
 * artifact supplies its draft shape (one section map per face), how a face's
 * markers address it, and the PUT.
 */
import { useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { ChevronLeftIcon } from "../../components/ui/icons";
import type { DocumentPreviewResponse } from "../../lib/document-preview-api";
import { useEditableField } from "../../lib/use-editable-field";
import { checkField, withinMaxLength } from "@shared/validators";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { cx } from "@shared/cx";
import chrome from "./settings.module.css";
import styles from "./studio-shell.module.css";

// Mirrors the server's 1-80 character template-name contract
// (go-service/internal/api/{quote,job_sheet}_templates.go). Format-only: the
// hook can't enforce required, so blank restores instead (see the title's
// onBlur below).
const templateNameRule = {
  predicate: (v: string) => withinMaxLength(v, 80),
  invalidMsg: "Keep this under 80 characters",
};

const message = (err: unknown) =>
  err instanceof Error ? err.message : String(err);

/**
 * Substitute the saved value while a URL field is half-typed: the preview
 * endpoint rejects a malformed link, and a rejected preview is how the studio
 * used to freeze. Every side-field draft the server can refuse goes through a
 * substitution like this, so the request is always one the server accepts.
 */
export function httpUrlOr(draft: string, saved: string): string {
  return draft === "" || /^https?:\/\/\S+$/.test(draft) ? draft : saved;
}

/**
 * One artifact's half of the studio. `draft` is the whole section payload of
 * the Template being edited — one map per face — so a two-faced artifact
 * commits both sets on every PUT, whichever face is on the sheet.
 */
export interface StudioArtifact<T, D> {
  template: { id: string; name: string };
  /** The Template's stored sections, as they arrived from the server. */
  initialSections: D;
  /** The section map the markers currently on the sheet address. */
  activeSections: (draft: D) => Record<string, boolean>;
  /** The draft with one of the active face's sections flipped. */
  toggleSection: (draft: D, key: string, next: boolean) => D;
  /** PUT the Template's name and sections. */
  save: (name: string, draft: D) => Promise<T>;
  /** Hand the saved row back to the roster. */
  onSaved: (saved: T) => void;
  /** The twin's root class — scopes the optimistic hide rules to this sheet. */
  docClass: string;
}

export function useTemplateStudio<T, D>({
  template,
  initialSections,
  activeSections,
  toggleSection,
  save,
  onSaved,
  docClass,
}: StudioArtifact<T, D>) {
  // The draft drives the preview optimistically: a removed section hides
  // instantly (hiddenCSS below) while the PUT and the debounced re-render are
  // in flight; a failed PUT reverts it.
  const [draft, setDraft] = useState<D>(initialSections);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Same autosave-on-blur controller as the equipment name cells and the
  // Event Details fields. The hook can't enforce non-blank (its onSave gets
  // null for empty input), so blank is handled at the blur site in
  // StudioShell by restoring the saved name instead of saving.
  const name = useEditableField(template.name, {
    validate: (v) => checkField(v, templateNameRule),
    onSave: (v) => {
      if (v !== null) void commitName(v);
    },
  });

  /** Adding/removing a section IS the commit; failure reverts the draft. */
  const commitSection = async (key: string, next: boolean) => {
    const prev = draft;
    const sections = toggleSection(prev, key, next);
    setDraft(sections);
    setSaveError(null);
    try {
      onSaved(await save(template.name, sections));
    } catch (err) {
      setDraft(prev);
      setSaveError(message(err));
    }
  };

  /** PUTs the rename; a failed save restores the stored name. */
  async function commitName(next: string) {
    try {
      onSaved(await save(next, draft));
      setSaveError(null);
    } catch (err) {
      name.setDraft(template.name);
      setSaveError(message(err));
    }
  }

  const sections = activeSections(draft);

  const handleSheetClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const remove = target.closest("[data-remove]")?.getAttribute("data-remove");
    if (remove && remove in sections) {
      void commitSection(remove, false);
      return;
    }
    const add = target.closest("[data-add]")?.getAttribute("data-add");
    if (add && add in sections) void commitSection(add, true);
  };

  // Hide optimistically-removed sections until the twin re-renders without
  // them; the add-strip pills carry data-add only, so they stay visible.
  // This one stays a <style> string rather than moving to the studio's
  // module: it is keyed on the Go twin's own root class and on which
  // sections this draft has removed, and a stylesheet can know neither.
  const hiddenCSS = Object.keys(sections)
    .filter((key) => !sections[key])
    .map(
      (key) =>
        `.${docClass} [data-section="${key}"],.${docClass} [data-part="${key}"]{display:none}`,
    )
    .join("");

  return { draft, saveError, name, handleSheetClick, hiddenCSS };
}

/**
 * The side fields' commit-on-blur controller: one company-settings key per
 * field, an unchanged value never saves, and a failure shows on the field
 * that caused it (DESIGN-SYSTEM.md, Error placement). `null` is a value the field
 * itself has already refused — it reports `invalidMessage` and saves nothing.
 */
export function useSettingFields<P>(onPatch: (patch: P) => Promise<void>) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldError = (field: string, text: string | null) =>
    setErrors((current) => {
      const next = { ...current };
      if (text === null) delete next[field];
      else next[field] = text;
      return next;
    });

  const commit = async (
    field: string,
    key: keyof P,
    value: string | number | null,
    saved: string | number,
    invalidMessage = "Enter a valid value.",
  ) => {
    if (value === null) {
      setFieldError(field, invalidMessage);
      return;
    }
    if (value === saved) {
      setFieldError(field, null);
      return;
    }
    setFieldError(field, null);
    try {
      await onPatch({ [key]: value } as P);
    } catch (err) {
      setFieldError(field, message(err));
    }
  };

  const fieldError = (field: string) =>
    errors[field] && (
      <InlineBanner variant="error">{errors[field]}</InlineBanner>
    );

  return { commit, fieldError };
}

export function StudioShell({
  backLabel,
  onBack,
  savedName,
  name,
  description,
  saveError,
  previewError,
  fieldsNote,
  fields,
  toolbar,
  hiddenCSS,
  result,
  loading,
  onSheetClick,
}: {
  backLabel: string;
  onBack: () => void;
  /** The stored name, restored when the title is blurred blank. */
  savedName: string;
  name: ReturnType<typeof useEditableField>;
  /** One line under the title naming what this face is. */
  description: string;
  saveError: string | null;
  previewError: string | null;
  /** One line above the side fields naming their scope. */
  fieldsNote: string;
  /** The labelled fields beside the sheet. */
  fields: React.ReactNode;
  /** Optional controls above the sheet (the quote studio's face switcher). */
  toolbar?: React.ReactNode;
  hiddenCSS: string;
  result: DocumentPreviewResponse | null;
  loading: boolean;
  onSheetClick: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <section>
      <button
        type="button"
        onClick={onBack}
        className={chrome.backLink}
      >
        <ChevronLeftIcon className={chrome.backIcon} />
        {backLabel}
      </button>
      <div className={styles.titleBlock}>
        <span className={styles.titleRow}>
          {/* No tooltip here: how to operate the studio is not tooltip
              material (DESIGN-SYSTEM.md, Tooltips). */}
          <input
            aria-label="Template name"
            className={cx(styles.title, name.error && styles.titleInvalid)}
            value={name.draft}
            onChange={(e) => name.setDraft(e.target.value)}
            onFocus={name.onFocus}
            onBlur={() => {
              name.onBlur();
              // The hook saves null for blank; a template must keep a name.
              if (name.draft.trim() === "") name.setDraft(savedName);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
        </span>
        {name.error && (
          <InlineBanner variant="error">{name.error}</InlineBanner>
        )}
        <p className={chrome.headerDescription}>{description}</p>
      </div>

      {saveError && (
        <Banner variant="error" className={chrome.bannerTight}>
          {saveError}
        </Banner>
      )}
      {previewError && (
        <Banner variant="error" className={chrome.bannerTight}>
          {previewError}
        </Banner>
      )}

      <div className={styles.layout}>
        <div className={styles.fieldsPane}>
          <p className={styles.fieldsNote}>{fieldsNote}</p>
          {fields}
        </div>

        <div className={styles.sheetPane}>
          {toolbar}
          {!result && !previewError && (
            <div className={chrome.buildingPreview}>
              Building preview…
            </div>
          )}
          {result?.html && (
            <div className={loading ? chrome.dimmed : undefined}>
              {hiddenCSS && <style>{hiddenCSS}</style>}
              {/* Sanctioned dangerouslySetInnerHTML: the fragment comes from
                  our own admin-authenticated document-preview endpoint, and
                  the Go twins (pdf.QuoteHTML / pdf.InvoiceHTML /
                  pdf.JobsheetHTML) escape every dynamic value. */}
              <div
                onClick={onSheetClick}
                dangerouslySetInnerHTML={{ __html: result.html }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
